import React, { useEffect, useState } from 'react';

import {
    Badge,
    BlockStack,
    Button,
    Card,
    Checkbox,
    IndexTable,
    InlineStack,
    Page,
    Select,
    Text,
    TextField,
} from '@shopify/polaris';
import useAdminApi from '../hooks/Api';
import usePartialState from '../hooks/PartialState';
import { useNavigate } from 'react-router-dom';
import AppModal from '../components/AppModal';
import CountryPicker from '../components/CountryPicker';
import ScopeBanner from '../components/ScopeBanner';
import FiscalNumberType from '../enums/PlanetFiscalNumberType';
import InvoiceNumberType from '../enums/PlanetInvoiceNumberType';

function LocationIndexPage() {
    const navigate = useNavigate();
    const { request } = useAdminApi();

    const [locations, setLocations] = useState([]);
    const [countries, setCountries] = useState([]);

    // Not persisted, unlike the location's own show_advanced_settings: there is no
    // shop-level record to hang it on, and it only gates a link.
    const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);

    const [modal, setModalState] = usePartialState({
        isOpen: false,
        title: 'Set-up Retail Location',
        processing: false,
        targetLocationId: null,
        targetLocationName: null,
        planetUsername: '',
        planetPassword: '',
        planetMerchantId: '',
        planetMerchantVatId: '',
        planetMerchantIsoCountry: '',
        isProduction: false,
        planetFiscalNumberType: FiscalNumberType.Empty.value,
        planetInvoiceNumberType: InvoiceNumberType.Receipt.value,
        disableCountrySelector: false,
        error: null,
    });

    async function handleLocationSetup(modal) {
        setModalState({ processing: true, error: null });

        const { ok, data } = await request.post('locations', {
            location_id: modal.targetLocationId,
            planet_merchant_username: modal.planetUsername,
            planet_merchant_password: modal.planetPassword,
            planet_merchant_id: modal.planetMerchantId,
            planet_merchant_vat_id: modal.planetMerchantVatId,
            is_production: modal.isProduction,
            planet_merchant_iso_country: modal.planetMerchantIsoCountry,
            planet_fiscal_number_type: modal.planetFiscalNumberType,
            planet_invoice_number_type: modal.planetInvoiceNumberType,
            line_splitting_enabled: getDefaultLineSplittingValue(modal.planetMerchantIsoCountry),
        });

        if (!ok) {
            setModalState({
                processing: false,
                error: data ? data.message : 'Could not save the location details at this time!',
            });
            return;
        }

        navigate(`/locations/${data.id}`);
    }

    function getDefaultLineSplittingValue(isoCountry) {
        const locationCountry = countries.find((country) => country.iso_numeric_code == isoCountry);
        if (['PT', 'ES', 'FR'].includes(locationCountry?.iso_short_code)) return true;
        else return false;
    }

    function openModal(targetLocationId, targetLocationName, targetLocationCountry) {
        setModalState({
            isOpen: true,
            processing: false,
            targetLocationId,
            targetLocationName,
            planetUsername: '',
            planetPassword: '',
            planetMerchantId: '',
            planetMerchantVatId: '',
            isProduction: false,
            planetMerchantIsoCountry: targetLocationCountry,
        });
        shopify.modal.show('locations-modal');
    }

    async function loadData() {
        const { data: locations } = await request('locations');
        setLocations(locations);

        const { data: countries } = await request('countries');
        setCountries(countries);
    }

    function handleClickedRow({ id, name, shop_location, iso_country }) {
        if (shop_location) {
            navigate(`/locations/${shop_location.id}`);
            return;
        }

        openModal(id, name, iso_country);
    }

    useEffect(() => {
        loadData();
        console.log(shopify);
    }, []);

    return (
        <>
            <Page title="Retail Locations">
                <BlockStack gap="400">
                    <ScopeBanner />

                    <Card padding="0">
                        <IndexTable
                            resourceName={{
                                singular: 'Location',
                                plural: 'Locations',
                            }}
                            itemCount={locations.length}
                            headings={[
                                { title: 'Retail Location Name' },
                                { title: 'Country' },
                                { title: 'Planet Status' },
                            ]}
                            selectable={false}
                        >
                            {locations.map(({ id, name, shop_location, country_name, iso_country }) => (
                                <IndexTable.Row
                                    id={id}
                                    key={id}
                                    onClick={() => handleClickedRow({ id, name, shop_location, iso_country })}
                                >
                                    <IndexTable.Cell>
                                        <Text variant="bodyMd" fontWeight="bold" as="span">
                                            {name}
                                        </Text>
                                    </IndexTable.Cell>
                                    <IndexTable.Cell>
                                        <Text>{country_name}</Text>
                                    </IndexTable.Cell>
                                    <IndexTable.Cell>
                                        {!!shop_location ? (
                                            <Badge tone="success" progress="complete">
                                                Configured
                                            </Badge>
                                        ) : (
                                            <Badge tone="read-only" progress="incomplete">
                                                Not Configured
                                            </Badge>
                                        )}
                                    </IndexTable.Cell>
                                </IndexTable.Row>
                            ))}
                        </IndexTable>
                    </Card>

                    {/* Category mapping is shop-wide rather than per-location, and the defaults
                    suit most merchants, so it sits behind the same disclosure the location
                    details page uses rather than in the nav. */}
                    <Checkbox
                        label="Show Advanced Settings"
                        helpText="Check to show advanced category mapping settings."
                        checked={showAdvancedSettings}
                        onChange={setShowAdvancedSettings}
                    />
                    {showAdvancedSettings && (
                        <Card>
                            <BlockStack gap="200">
                                <Text variant="headingMd" as="h3">
                                    Category Mapping
                                </Text>
                                <Text variant="bodyMd" tone="subdued">
                                    Planet needs an article code on every line item in Spain, Portugal and France.
                                    Sensible defaults are already applied, so change these only if a category reports
                                    the wrong one.
                                </Text>
                                <InlineStack>
                                    <Button onClick={() => navigate('/category-mappings')}>
                                        Manage category mapping
                                    </Button>
                                </InlineStack>
                            </BlockStack>
                        </Card>
                    )}
                </BlockStack>
            </Page>

            <AppModal
                title={modal.title}
                cancelLabel="Cancel"
                confirmLabel="Set-up Retail Location"
                confirmAction={() => handleLocationSetup(modal)}
                confirmEnabled={
                    !modal.processing &&
                    modal.planetUsername.length > 0 &&
                    modal.planetPassword.length > 0 &&
                    modal.planetMerchantId.length > 0 &&
                    modal.planetMerchantVatId.length > 0 &&
                    modal.planetMerchantIsoCountry.length > 0
                }
                processing={modal.processing}
            >
                <BlockStack gap="400">
                    <InlineStack gap="100">
                        <Text>Please input your Planet credentials for the following Retail Location:</Text>
                        <Text fontWeight="bold">{modal.targetLocationName}</Text>
                    </InlineStack>
                    <TextField
                        label="Planet Merchant ID"
                        value={modal.planetMerchantId}
                        onChange={(value) => setModalState({ planetMerchantId: value })}
                        autoComplete="off"
                        readOnly={modal.processing}
                    />
                    <TextField
                        label="Planet Merchant VAT ID"
                        value={modal.planetMerchantVatId}
                        onChange={(value) => setModalState({ planetMerchantVatId: value })}
                        autoComplete="off"
                        readOnly={modal.processing}
                    />
                    {modal.disableCountrySelector || (
                        <CountryPicker
                            label="Planet ISO Country"
                            countries={countries}
                            value={modal.planetMerchantIsoCountry}
                            onChange={(value) => setModalState({ planetMerchantIsoCountry: value })}
                        />
                    )}
                    <TextField
                        label="Planet POS Username"
                        value={modal.planetUsername}
                        onChange={(value) => setModalState({ planetUsername: value })}
                        autoComplete="off"
                        readOnly={modal.processing}
                    />
                    <TextField
                        label="Planet POS Password"
                        value={modal.planetPassword}
                        onChange={(value) => setModalState({ planetPassword: value })}
                        autoComplete="off"
                        type="password"
                        readOnly={modal.processing}
                    />
                    <Text>
                        You will be able to configure advanced invoicing and fiscal number settings after saving the
                        location.
                    </Text>
                    <Checkbox
                        label="Is Production?"
                        checked={modal.isProduction}
                        onChange={(value) => setModalState({ isProduction: value })}
                    />
                    {modal.error && (
                        <InlineStack gap="100">
                            <Text tone="critical" fontWeight="bold">
                                Error:
                            </Text>
                            <Text tone="critical">{modal.error}</Text>
                        </InlineStack>
                    )}
                </BlockStack>
            </AppModal>
        </>
    );
}

export default LocationIndexPage;
